import React, { useEffect, useState } from "react";
import AxiosInstance from "../authorization/AxiosInstance";
import ImageUploader from "./ImageUploader";

const EMPTY = { title: "", subtitle: "", content: "", imageUrl: "", order: 0 };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <span className="font-bold text-lg text-gray-800">{title}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="overflow-y-auto px-6 py-4 flex-1">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", rows }) {
  const cls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400";
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {rows ? <textarea name={name} value={value} onChange={onChange} rows={rows} className={cls} />
             : <input name={name} type={type} value={value} onChange={onChange} className={cls} />}
    </div>
  );
}

export default function LeadershipAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState(null);

  const fetch = () => {
    setLoading(true);
    AxiosInstance.get("/leadership").then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === "order" ? Number(value) : value }));
  };

  const openCreate = () => { setForm(EMPTY); setModal("create"); setError(null); };
  const openEdit = item => {
    setForm({ title: item.title || "", subtitle: item.subtitle || "", content: item.content || "", imageUrl: item.imageUrl || "", order: item.order ?? 0 });
    setEditId(item.id); setModal("edit"); setError(null);
  };
  const closeModal = () => { setModal(null); setEditId(null); setError(null); };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      if (modal === "create") await AxiosInstance.post("/leadership", form);
      else await AxiosInstance.patch(`/leadership/${editId}`, form);
      fetch(); closeModal();
    } catch (e) { setError(e?.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    try { await AxiosInstance.delete(`/leadership/${id}`); setDeleteId(null); fetch(); }
    catch { setError("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Leadership Cards</h2>
        <button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">
          + Add Card
        </button>
      </div>

      {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

      {loading ? <div className="text-gray-400 text-sm">Loading…</div> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 uppercase text-xs">
                <th className="text-left px-4 py-3 font-semibold">#</th>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Subtitle</th>
                <th className="text-left px-4 py-3 font-semibold">Order</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No items</td></tr>}
              {items.map((item, i) => (
                <tr key={item.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{item.title}</td>
                  <td className="px-4 py-3 text-gray-500">{item.subtitle || "—"}</td>
                  <td className="px-4 py-3 text-gray-500">{item.order}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(item)} className="text-blue-600 hover:text-blue-800 text-xs font-semibold">Edit</button>
                      {deleteId === item.id ? (
                        <span className="flex gap-1">
                          <button onClick={() => handleDelete(item.id)} className="text-red-600 text-xs font-semibold">Confirm</button>
                          <button onClick={() => setDeleteId(null)} className="text-gray-400 text-xs">Cancel</button>
                        </span>
                      ) : (
                        <button onClick={() => setDeleteId(item.id)} className="text-red-500 hover:text-red-700 text-xs font-semibold">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal === "create" ? "Add Card" : "Edit Card"} onClose={closeModal}>
          {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
          <Field label="Title *" name="title" value={form.title} onChange={handleChange} />
          <Field label="Subtitle" name="subtitle" value={form.subtitle} onChange={handleChange} />
          <Field label="Content (full page body)" name="content" value={form.content} onChange={handleChange} rows={6} />
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
            <ImageUploader
              value={form.imageUrl}
              onChange={(url) => setForm(f => ({ ...f, imageUrl: url }))}
              folder="leadership"
            />
          </div>
          <Field label="Order" name="order" value={form.order} onChange={handleChange} type="number" />
          <div className="flex justify-end gap-3 mt-2">
            <button onClick={closeModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.title} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
